import { useEffect, useState } from 'react';
import { X, Printer, Wallet } from '../../lib/icons';
import { toast } from 'sonner';
import { ajouterMouvement, fetchFinances } from '../../lib/dataProvider';
import { getBranding } from '../../lib/branding';
import { EmissionFacture } from '../print/EmissionFacture';
import { fmt } from '../shared/badges';

const TVA = 0.19;

export const FactureTrituration = ({ releve, onClose, onEncaisse }) => {
  const b = getBranding();
  const [compteId, setCompteId] = useState('esp');
  const [comptes, setComptes] = useState([]);
  const [encaisse, setEncaisse] = useState(false);

  // Référence interne STABLE, indépendante de la date : réouvrir la prestation
  // un autre jour ne change plus rien. Les numéros FT- historiques déjà présents
  // dans les paiements sont adoptés tels quels (aucune renumérotation).
  const dernierDepot = releve.depots[releve.depots.length - 1]?.id ?? '0';
  const refInterne = `TRIT-${releve.tiersId ?? releve.id ?? 'CLI'}-${dernierDepot}`;
  const [numero, setNumero] = useState(refInterne);

  const ht = releve.fraisTrituration;
  const tva = Number((ht * TVA).toFixed(2));
  const ttc = Number((ht + tva).toFixed(2));

  useEffect(() => {
    fetchFinances().then((fin) => {
      setComptes(fin.comptes);
      // 1. Un paiement existant sur la référence stable ou sur un ancien numéro FT-
      //    lié au même dépôt est reconnu : la facture reste « encaissée ».
      const historique = fin.mouvements.find(
        (m) => m.piece === refInterne || (String(m.piece ?? '').startsWith('FT-') && String(m.piece).endsWith(`-${dernierDepot}`))
      );
      if (historique) {
        setNumero(historique.piece);
        setEncaisse(true);
      }
    });
  }, [refInterne, dernierDepot]);

  const encaisser = async () => {
    const fin = await fetchFinances();
    if (fin.mouvements.some((m) => m.piece === numero)) {
      setEncaisse(true);
      return toast.error('Cette facture a déjà été encaissée.');
    }
    const { error } = await ajouterMouvement({
      date: new Date().toISOString().slice(0, 10),
      type: 'Alimentation',
      compteId,
      compteCibleId: null,
      categorie: 'Frais de trituration',
      beneficiaire: releve.nom,
      libelle: `Facture trituration ${numero} — ${releve.nom}`,
      piece: numero,
      justificatif: null,
      montant: ttc,
    });
    if (error) return toast.error(error);
    setEncaisse(true);
    toast.success(`${fmt(ttc, 2)} TND encaissés en caisse`);
    onEncaisse?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="facture-dialog">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border-2 border-slate-900 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4 print:hidden">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
              Prestation de trituration
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">{numero}</p>
          </div>
          <button
            type="button"
            data-testid="facture-fermer"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div id="facture-print" className="px-5 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-slate-900 pb-4">
            <div className="flex items-start gap-3">
              {b.logo && <img src={b.logo} alt="Logo" className="h-16 w-16 object-contain" />}
              <div>
                <p className="font-heading text-base font-black tracking-tight text-slate-900">{b.nom}</p>
                <p className="font-data text-[11px] text-slate-600">{b.slogan}</p>
                <p className="mt-1 font-data text-[11px] text-slate-600">{b.adresse}</p>
                <p className="font-data text-[11px] text-slate-600">
                  Tél. {b.tel}
                  {b.tel2 ? ` / ${b.tel2}` : ''} · {b.email}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Client</p>
              <p data-testid="facture-client" className="font-heading text-sm font-bold text-slate-900">
                {releve.nom}
              </p>
              <p className="font-data text-[11px] text-slate-500">
                {new Date().toISOString().slice(0, 10)}
              </p>
              <p className="mt-2 border border-slate-400 px-2 py-1 font-data text-[10px] uppercase tracking-[0.08em] text-slate-700">
                Matricule fiscal : {b.mf || '________________'}
              </p>
            </div>
          </div>

          <table className="mt-4 w-full border-collapse text-left" data-testid="facture-lignes">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Lot', 'Date', 'Olives (Kg)', 'Tarif (TND/kg)', 'Montant (TND)'].map((c) => (
                  <th key={c} className="px-2 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {releve.depots.map((d) => (
                <tr key={d.id} data-testid={`facture-ligne-${d.id}`} className="border-b border-slate-100">
                  <td className="px-2 py-2 font-data text-sm font-semibold text-slate-900">{d.id}</td>
                  <td className="px-2 py-2 font-data text-sm text-slate-700">{d.date}</td>
                  <td className="tabular px-2 py-2 font-data text-sm text-slate-900">{fmt(d.olives)}</td>
                  <td className="tabular px-2 py-2 font-data text-sm text-slate-700">{fmt(d.tarif ?? 0, 3)}</td>
                  <td className="tabular px-2 py-2 font-data text-sm font-semibold text-slate-900">{fmt(d.trituration, 3)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <dl className="mt-4 ml-auto w-full max-w-xs space-y-1 font-data text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Total HT</dt>
              <dd data-testid="facture-ht" className="tabular font-semibold text-slate-900">{fmt(ht, 2)} TND</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">TVA 19 %</dt>
              <dd data-testid="facture-tva" className="tabular text-slate-900">{fmt(tva, 2)} TND</dd>
            </div>
            <div className="flex justify-between border-t border-slate-900 pt-1">
              <dt className="font-bold uppercase tracking-[0.06em] text-slate-900">Total TTC</dt>
              <dd data-testid="facture-ttc" className="tabular text-base font-bold text-slate-900">{fmt(ttc, 2)} TND</dd>
            </div>
          </dl>

          <p className="mt-4 border-t border-slate-200 pt-3 font-data text-[11px] text-slate-500">
            Huile déposée au compte du client : {fmt(releve.depose, 1)} L · retirée {fmt(releve.retire, 1)} L ·
            solde en cuve {fmt(releve.solde, 1)} L.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 px-5 py-4 print:hidden">
          <div className="w-full">
            <EmissionFacture
              operation={{
                type: 'prestation_trituration',
                id: refInterne,
                libelle: 'Prestation de trituration',
                date: new Date().toISOString().slice(0, 10),
                tiersId: releve.tiersId,
                tiers: releve.nom,
                referenceInterne: numero,
                total: ttc,
                lignes: [
                  ['Frais de trituration HT', `${fmt(ht, 3)} TND`],
                  ['TVA 19 %', `${fmt(tva, 3)} TND`],
                ],
                totaux: [['Total TTC', `${fmt(ttc, 3)} TND`]],
                mentions: 'Prestation de trituration — montants en dinars tunisiens.',
              }}
            />
          </div>
          <button
            type="button"
            data-testid="facture-imprimer"
            onClick={() => window.print()}
            className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <Printer size={15} /> Imprimer
          </button>
          <select
            data-testid="facture-compte"
            value={compteId}
            onChange={(e) => setCompteId(e.target.value)}
            className="h-11 border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
          >
            {comptes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="facture-encaisser"
            disabled={encaisse || ht <= 0}
            onClick={encaisser}
            className={`flex h-11 flex-1 items-center justify-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 ${
              encaisse || ht <= 0 ? 'cursor-not-allowed bg-slate-300' : 'bg-emerald-700 hover:bg-emerald-600'
            }`}
          >
            <Wallet size={16} /> {encaisse ? 'Encaissé' : 'Encaisser en caisse'}
          </button>
        </div>
      </div>
    </div>
  );
};
